import { Button } from "@gettactic/components/src/primitives/button";
import { useTacticHooks } from "@gettactic/hooks";
import { ArrowLeft, DoorOpen, MapPin, ScanLine } from "lucide-react";
import React from "react";
import { finalizeImportedMap } from "../../../../utils/finalizeImportedMap";
import {
  openFloorplanPdf,
  renderFloorplanPdfPage
} from "../../../../utils/floorplanPdf";
import { normalizeFloorplanImage } from "../../../../utils/normalizeFloorplanImage";
import { prepareImportedMap } from "../../../../utils/prepareImportedMap";
import { readImportedMapJson } from "../../../../utils/readImportedMapJson";
import {
  type FloorplanComparison,
  requestFloorplanComparison
} from "../../../../utils/requestFloorplanComparison";
import type {
  RetryBounds,
  RetryScope
} from "../../../../utils/requestRegionalFloorplan";
import {
  cancelRegionalFloorplan,
  type RegionalImportSnapshot,
  requestRegionalFloorplan
} from "../../../../utils/requestRegionalFloorplan";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from "../../../ui/dialog";
import { MapConstructionPreview } from "../../../ViewSVG/MapConstructionPreview";
import { useConstructionAnimation } from "../../animation/ConstructionAnimationContext";
import {
  useMapCanvas,
  useMapCanvasStage
} from "../../contexts/MapCanvasContext";
import type { MapCanvasValue } from "../../contexts/MapCanvasContext/MapCanvasContext.types";
import { useMapEditorActions } from "../../contexts/MapEditorContext";
import {
  FastImportSourceOverlay,
  FastImportTiles
} from "./FastImportSourceOverlay";
import { FloorplanImportPill } from "./FloorplanImportPill";
import { FloorplanImportSetup } from "./FloorplanImportSetup";
import { FloorplanImportStatus } from "./FloorplanImportStatus";
import { ImportNotes } from "./ImportNotes";
import {
  ImportRegionProgress,
  ImportRegionSourceOverlay
} from "./ImportRegionProgress";
import { ImportRetryBox } from "./ImportRetryBox";
import { ImportRoomHighlightOverlay } from "./ImportRoomHighlightOverlay";

interface ImportSettings {
  isFast: boolean;
  isRegions: boolean;
  isWallsOnly: boolean;
  isHighDetail: boolean;
}

/** One request to the importer; it may start before the user asks to see it. */
interface ImportRun {
  controller: AbortController;
  file: File;
  png: Blob;
  settingsKey: string;
  isFast: boolean;
  isRevealed: boolean;
  isFinished: boolean;
  latest?: FloorplanComparison;
  failure?: string;
}

interface ImportResult {
  map: MapCanvasValue;
  warnings: string[];
  model: string;
  elapsedMs?: number;
  partial?: boolean;
}

export function AutoImportDialog({
  open,
  onOpenChange,
  importMode = "ai"
}: {
  importMode?: "ai" | "json";
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { api } = useTacticHooks();
  const { animate } = useConstructionAnimation();
  const jsonInput = React.useRef<HTMLInputElement>(null);
  const [importedJson, setImportedJson] = React.useState(false);
  const [replay, setReplay] = React.useState(0);
  const [sourcePng, setSourcePng] = React.useState<Blob>();
  const [applying, setApplying] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState({
    completed: 0,
    total: 0
  });
  const {
    loadMapValue,
    loadMapValueRecordsHistory,
    shapesRef,
    setLastSpotLabel
  } = useMapCanvas();
  const { setShapes, setSelectedIds } = useMapEditorActions();
  const { centerStage } = useMapCanvasStage();
  const [boxSelect, setBoxSelect] = React.useState(false);
  const [markerOnTable, setMarkerOnTable] = React.useState(true);
  const [highlightRoom, setHighlightRoom] = React.useState(false);
  const [deskIds, setDeskIds] = React.useState<Set<string>>(new Set());
  const [file, setFile] = React.useState<File>();
  const [pdf, setPdf] = React.useState<{
    file: File;
    document: Awaited<ReturnType<typeof openFloorplanPdf>>;
  }>();
  const [pdfPage, setPdfPage] = React.useState(1);
  const [loadingPdf, setLoadingPdf] = React.useState(false);
  const [preparingPdf, setPreparingPdf] = React.useState(false);
  const isPdf =
    !!file && (/\.pdf$/i.test(file.name) || file.type === "application/pdf");
  const [selectedTables, setSelectedTables] = React.useState<Set<string>>(
    new Set()
  );
  const [source, setSource] = React.useState<string>();
  const [recognition, setResult] = React.useState<ImportResult>();
  const [wallsOnly, setWallsOnly] = React.useState(false);
  const [processPage, setProcessPage] = React.useState(false);
  const previewRevision = React.useRef("");
  const [highDetail, setHighDetail] = React.useState(false);
  const [useRegions, setUseRegions] = React.useState(true);
  const [fastMode, setFastMode] = React.useState(true);
  // A fast import keeps running when the dialog closes; the toolbar pill
  // reports it, and says when the finished map is waiting to be reviewed.
  const [isFastRunning, setIsFastRunning] = React.useState(false);
  const isOpenRef = React.useRef(open);
  React.useEffect(() => {
    isOpenRef.current = open;
  }, [open]);
  const [hasUnseenResult, setHasUnseenResult] = React.useState(false);
  const arrivals = React.useRef(new Set<string>());
  // Start preparing the image as soon as it is chosen, not on Generate.
  const normalized = React.useRef<{ file: File; png: Promise<Blob> }>(
    undefined
  );
  // Fast imports start as soon as the image is ready; Generate then just
  // reveals the run, unless a setting changed in between.
  const importRun = React.useRef<ImportRun | undefined>(undefined);
  const settingsKey = JSON.stringify({
    isFast: fastMode,
    isRegions: useRegions,
    isWallsOnly: wallsOnly,
    isHighDetail: highDetail
  } satisfies ImportSettings);
  const settingsRef = React.useRef<ImportSettings>(JSON.parse(settingsKey));
  React.useEffect(() => {
    settingsRef.current = JSON.parse(settingsKey);
    const run = importRun.current;
    if (run && !run.isRevealed && run.settingsKey !== settingsKey) {
      run.controller.abort();
      importRun.current = undefined;
    }
  }, [settingsKey]);
  const [comparison, setComparison] = React.useState<FloorplanComparison>();
  const [regional, setRegional] = React.useState<RegionalImportSnapshot>();
  const [selectedRegion, setSelectedRegion] = React.useState("perimeter");
  const [retryScope, setRetryScope] = React.useState<RetryScope>("all");
  const [retryDrawing, setRetryDrawing] = React.useState(false);
  const [retryBounds, setRetryBounds] = React.useState<RetryBounds>();
  function chooseRegion(id: string) {
    setSelectedRegion(id);
    setRetryBounds(undefined);
    setRetryDrawing(false);
    setRetryScope("all");
  }
  const regionalRevision = React.useRef(-1);
  const pendingRegionalSession = React.useRef<string | undefined>(undefined);
  function cancelAbandonedRegion() {
    if (pendingRegionalSession.current) {
      void cancelRegionalFloorplan(pendingRegionalSession.current);
      pendingRegionalSession.current = undefined;
    }
  }
  const [sourceDimensions, setSourceDimensions] = React.useState({
    width: 1,
    height: 1
  });
  function updateRegional(snapshot: RegionalImportSnapshot) {
    setRegional(snapshot);
    pendingRegionalSession.current =
      snapshot.status === "in_progress" ? snapshot.session : undefined;
    setProgress(
      `${snapshot.parts.filter((p) => p.status === "completed").length}/${snapshot.parts.length} areas ready`
    );
    if (snapshot.map && regionalRevision.current !== snapshot.revision) {
      regionalRevision.current = snapshot.revision;
      setResult({
        map: snapshot.map,
        warnings: snapshot.warnings,
        model: snapshot.model,
        elapsedMs: snapshot.elapsedMs,
        partial: snapshot.status !== "completed"
      });
    }
    if (snapshot.error) setError(snapshot.error);
  }
  async function retryRegion(resume = false) {
    if (!regional || busy || applying) return;
    const controller = new AbortController();
    requestRef.current?.abort();
    requestRef.current = controller;
    setBusy(true);
    setError("");
    setDeskIds(new Set());
    setSelectedTables(new Set());
    setResult((previous) =>
      previous ? { ...previous, partial: true } : previous
    );
    try {
      await requestRegionalFloorplan({
        session: regional.session,
        retry: resume ? undefined : retryBounds ? "selection" : selectedRegion,
        scope: resume
          ? undefined
          : selectedRegion === "perimeter" && !retryBounds
            ? "walls"
            : retryScope,
        bounds: resume ? undefined : retryBounds,
        signal: controller.signal,
        onUpdate: updateRegional
      });
    } catch (e) {
      if (!controller.signal.aborted)
        setError(e instanceof Error ? e.message : "Unable to retry area");
    } finally {
      if (requestRef.current === controller) setBusy(false);
    }
  }
  const result = React.useMemo(() => {
    if (!recognition) return undefined;
    const configured = finalizeImportedMap(recognition.map, deskIds, {
      markerOnTable,
      highlightRoom
    });
    return {
      ...recognition,
      map: configured.map,
      warnings: [...recognition.warnings, ...configured.warnings]
    };
  }, [recognition, deskIds, markerOnTable, highlightRoom]);
  const durationSeconds = Math.max(
    1,
    Math.round((result?.elapsedMs || 0) / 1000)
  );
  const [expanded, setExpanded] = React.useState(false);
  const [overlay, setOverlay] = React.useState(false);
  const previewMap = React.useMemo<MapCanvasValue | undefined>(() => {
    if (!result) return undefined;
    if (!overlay || !source) return result.map;
    return {
      ...result.map,
      shapes: [
        {
          id: "import-preview-reference",
          type: "image",
          layerType: "background",
          x: 0,
          y: 0,
          width: result.map.editingZoneWidth,
          height: result.map.editingZoneHeight,
          path: source
        },
        ...result.map.shapes.map((shape) =>
          shape.type === "wall-path" ? { ...shape, color: "#2563eb" } : shape
        )
      ]
    };
  }, [result, overlay, source]);
  const [error, setError] = React.useState("");
  const [progress, setProgress] = React.useState("Reading the floorplan…");
  const [busy, setBusy] = React.useState(false);
  const requestRef = React.useRef<AbortController | null>(null);
  React.useEffect(
    () => () => {
      requestRef.current?.abort();
      cancelAbandonedRegion();
    },
    []
  );
  React.useEffect(
    () => () => {
      if (source) URL.revokeObjectURL(source);
    },
    [source]
  );
  React.useEffect(() => {
    if (open) {
      setHasUnseenResult(false);
      return;
    }
    if (isFastRunning) return;
    requestRef.current?.abort();
    cancelAbandonedRegion();
    setBusy(false);
  }, [open, isFastRunning]);

  React.useEffect(() => {
    setPdf(undefined);
    setLoadingPdf(false);
    if (!file || !isPdf || !open) return;
    let cancelled = false;
    let loaded: Awaited<ReturnType<typeof openFloorplanPdf>> | undefined;
    setLoadingPdf(true);
    openFloorplanPdf(file)
      .then((document) => {
        loaded = document;
        if (cancelled) {
          void document.destroy();
          return;
        }
        setPdf({ file, document });
      })
      .catch((cause) => {
        if (!cancelled)
          setError(
            cause instanceof Error ? cause.message : "Unable to read PDF"
          );
      })
      .finally(() => {
        if (!cancelled) setLoadingPdf(false);
      });
    return () => {
      cancelled = true;
      if (loaded) void loaded.destroy();
    };
  }, [file, isPdf, open]);
  React.useEffect(() => {
    if (!pdf || pdf.file !== file || !open) return;
    const controller = new AbortController();
    setPreparingPdf(true);
    setSourcePng(undefined);
    renderFloorplanPdfPage(pdf.document, pdfPage, controller.signal)
      .then((png) => {
        if (controller.signal.aborted) return;
        setSourcePng(png);
        setSource(URL.createObjectURL(png));
      })
      .catch((cause) => {
        if (!controller.signal.aborted)
          setError(
            cause instanceof Error ? cause.message : "Unable to render PDF page"
          );
      })
      .finally(() => {
        if (!controller.signal.aborted) setPreparingPdf(false);
      });
    return () => controller.abort();
  }, [pdf, pdfPage, file, open]);

  function chooseFile(next: File) {
    if (!/\.(png|jpe?g|webp|svg|pdf)$/i.test(next.name)) {
      setError("Choose a PNG, JPG, WebP, SVG or PDF file.");
      return;
    }
    if (next.size > 20 * 1024 * 1024) {
      setError("Choose a file smaller than 20 MB.");
      return;
    }
    cancelAbandonedRegion();
    setImportedJson(false);
    setReplay(0);
    setFile(next);
    normalized.current = undefined;
    // A new plan replaces any import of the previous one, shown or not, so a
    // finished old run can't later offer itself for review.
    const previous = importRun.current;
    if (previous && !previous.isFinished) previous.controller.abort();
    importRun.current = undefined;
    setBusy(false);
    setIsFastRunning(false);
    setHasUnseenResult(false);
    if (!/\.pdf$/i.test(next.name) && next.type !== "application/pdf") {
      const png = normalizeFloorplanImage(next);
      normalized.current = { file: next, png };
      png
        .then((ready) => {
          const current = importRun.current;
          const isRunning =
            !!current &&
            !current.isFinished &&
            !current.controller.signal.aborted;
          if (
            normalized.current?.file !== next ||
            isRunning ||
            !isOpenRef.current ||
            !settingsRef.current.isFast
          )
            return;
          startRun(next, ready);
        })
        // Generate awaits (and reports) this; don't flag it as unhandled here.
        .catch(() => undefined);
    }
    setComparison(undefined);
    setPdfPage(1);
    setSourcePng(undefined);
    setSelectedTables(new Set());
    setResult(undefined);
    setRegional(undefined);
    setSource(undefined);
    setError("");
  }

  function showSnapshot(snapshot: FloorplanComparison) {
    setComparison(snapshot);
    setProgress("Reading walls and arranging your map…");
    if (snapshot.candidates.length !== 1) return;
    const candidate = snapshot.candidates[0];
    // Counts change while a fast job streams desks, before its status does.
    const revision = JSON.stringify([
      candidate.status,
      candidate.areas.map((a) => [
        a.status,
        a.counts?.desks,
        a.counts?.walls,
        a.counts?.labels
      ])
    ]);
    if (!candidate.map || revision === previewRevision.current) return;
    previewRevision.current = revision;
    setResult({
      map: candidate.map,
      warnings: candidate.warnings,
      model: candidate.model,
      elapsedMs: candidate.elapsedMs,
      partial: candidate.status !== "completed"
    });
  }

  function startRun(forFile: File, png: Blob): ImportRun {
    const controller = new AbortController();
    requestRef.current?.abort();
    requestRef.current = controller;
    previewRevision.current = "";
    arrivals.current = new Set();
    const settings = settingsRef.current;
    const run: ImportRun = {
      controller,
      file: forFile,
      png,
      settingsKey: JSON.stringify(settings),
      isFast: settings.isFast,
      isRevealed: false,
      isFinished: false
    };
    importRun.current = run;
    void requestFloorplanComparison(
      png,
      controller.signal,
      (snapshot) => {
        run.latest = snapshot;
        if (run.isRevealed) showSnapshot(snapshot);
      },
      [
        settings.isFast
          ? "openai-sol-fast"
          : settings.isRegions
            ? "openai-sol"
            : "openai-sol-whole"
      ],
      settings.isRegions || settings.isFast,
      settings.isWallsOnly ? "walls" : "all",
      settings.isHighDetail ? "high" : "medium"
    )
      .catch((cause) => {
        if (controller.signal.aborted) return;
        run.failure = cause instanceof Error ? cause.message : "Import failed";
        if (run.isRevealed) setError(run.failure);
      })
      .finally(() => {
        run.isFinished = true;
        if (!run.isRevealed || requestRef.current !== controller) return;
        setBusy(false);
        setIsFastRunning(false);
        if (!isOpenRef.current && !controller.signal.aborted)
          setHasUnseenResult(true);
      });
    return run;
  }

  function revealRun(run: ImportRun) {
    run.isRevealed = true;
    setSource(URL.createObjectURL(run.png));
    setSourcePng(run.png);
    setBusy(!run.isFinished);
    setIsFastRunning(run.isFast && !run.isFinished);
    if (run.latest) showSnapshot(run.latest);
    if (run.failure) setError(run.failure);
  }

  async function generate() {
    if (!file) return;
    setProcessPage(true);
    setOverlay(true);
    setExpanded(true);
    setHasUnseenResult(false);
    setProgress("Uploading the floorplan…");
    setError("");
    setResult(undefined);
    setComparison(undefined);
    cancelAbandonedRegion();
    setRegional(undefined);
    setRetryBounds(undefined);
    setRetryDrawing(false);
    regionalRevision.current = -1;
    setDeskIds(new Set());
    setSelectedTables(new Set());
    const revealEarlyRun = () => {
      const early = importRun.current;
      if (
        !early ||
        early.isRevealed ||
        early.file !== file ||
        early.settingsKey !== settingsKey ||
        early.controller.signal.aborted ||
        early.failure
      )
        return false;
      revealRun(early);
      return true;
    };
    if (revealEarlyRun()) return;
    setBusy(true);
    setIsFastRunning(fastMode);
    try {
      const png = isPdf
        ? sourcePng
        : normalized.current?.file === file
          ? await normalized.current.png
          : await normalizeFloorplanImage(file);
      if (!png) throw new Error("Wait for the PDF page preview to finish");
      if (!isOpenRef.current) {
        setBusy(false);
        setIsFastRunning(false);
        return;
      }
      // The early start may have begun while the image was being prepared.
      if (revealEarlyRun()) return;
      revealRun(startRun(file, png));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Import failed");
      setBusy(false);
      setIsFastRunning(false);
    }
  }

  function cancelImport() {
    requestRef.current?.abort();
    setBusy(false);
  }

  async function apply() {
    if (
      !result ||
      result.partial ||
      busy ||
      (!sourcePng && !importedJson) ||
      applying
    )
      return;
    setApplying(true);
    setError("");
    let isApplied = false;
    try {
      const map = await prepareImportedMap(
        result.map,
        sourcePng,
        async (file) => {
          const response = await api.client.organizations.getUploadUrl(
            {
              type:
                file.type === "image/svg+xml"
                  ? "map:furniture"
                  : "map:background",
              format: file.type === "image/svg+xml" ? "svg" : "png"
            },
            file
          );
          const { upload_url, url } = response?.result || {};
          if (!upload_url || !url)
            throw new Error("Unable to prepare the map asset upload");
          const uploaded = await fetch(upload_url, {
            method: "PUT",
            body: file
          });
          if (!uploaded.ok)
            throw new Error(
              "Map asset upload failed. The current canvas has not changed"
            );
          return url;
        },
        (completed, total) => setUploadProgress({ completed, total })
      );
      if (!loadMapValueRecordsHistory) setShapes(map.shapes);
      loadMapValue(map);
      setLastSpotLabel(null);
      setSelectedIds([]);
      isApplied = true;
      // Stay "applying" until the dialog closes, so a second click during
      // these two frames cannot upload and load the map again.
      requestAnimationFrame(() => {
        centerStage();
        requestAnimationFrame(() => {
          animate(map);
          onOpenChange(false);
          setApplying(false);
        });
      });
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Unable to apply the map"
      );
    } finally {
      if (!isApplied) setApplying(false);
    }
  }

  async function importJson(file: File) {
    setError("");
    try {
      if (file.size > 20 * 1024 * 1024)
        throw Error("Map JSON must be under 20 MB");
      const map = readImportedMapJson(await file.text());
      // Check the same local finalization used by the preview before committing state.
      finalizeImportedMap(map);
      cancelAbandonedRegion();
      setFile(undefined);
      setSource(undefined);
      setSourcePng(undefined);
      setComparison(undefined);
      setRegional(undefined);
      setDeskIds(new Set());
      setSelectedTables(new Set());
      setOverlay(false);
      setExpanded(true);
      setImportedJson(true);
      setResult({ map, warnings: [], model: "saved-json" });
      setReplay(0);
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "Unable to read map JSON"
      );
    }
  }

  function download() {
    if (!result || result.partial || busy) return;
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(result.map, null, 2)], {
        type: "application/json"
      })
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = "imported-map.json";
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  const pillStatus =
    !open && busy && isFastRunning
      ? "running"
      : !open && hasUnseenResult
        ? error || !result || result.partial
          ? "failed"
          : "ready"
        : undefined;

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!applying) onOpenChange(value);
      }}
    >
      {/* Radix Root renders this inline, next to the toolbar button. */}
      {pillStatus && (
        <FloorplanImportPill
          status={pillStatus}
          startedAt={comparison?.startedAt}
          onOpen={() => onOpenChange(true)}
        />
      )}
      <DialogContent
        className={`max-h-[90vh] overflow-y-auto rounded-2xl ${processPage || result ? "max-w-6xl" : "max-w-3xl"}`}
        onEscapeKeyDown={(event) => {
          if (
            applying ||
            busy ||
            boxSelect ||
            selectedTables.size ||
            retryDrawing ||
            retryBounds
          ) {
            event.preventDefault();
            if (!applying && !busy) {
              setBoxSelect(false);
              setSelectedTables(new Set());
              setRetryDrawing(false);
              setRetryBounds(undefined);
            }
          }
        }}
      >
        {processPage && importMode === "ai" && (
          <button
            type="button"
            disabled={applying}
            onClick={() => setProcessPage(false)}
            className="flex w-fit items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-neutral-600 hover:bg-neutral-100 focus-visible:outline-blue-500"
          >
            <ArrowLeft size={16} /> Back to setup
          </button>
        )}
        <DialogHeader>
          <DialogTitle>
            {importMode === "json"
              ? "Import saved map"
              : processPage
                ? busy
                  ? "Watch your map take shape"
                  : "Review your map"
                : "Turn your floorplan into a map"}
          </DialogTitle>
        </DialogHeader>
        <p className="text-neutral-600 text-sm">
          {importMode === "json"
            ? "Load a saved map JSON, review it, then apply it to the editor."
            : processPage
              ? "Completed sections appear over your floorplan as they arrive. Review the finished map before applying it."
              : "Start with your floorplan. We’ll trace the structure and place editable objects, ready for your final touches."}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          {importMode === "json" && (
            <>
              <Button
                disabled={busy || applying}
                onClick={() => jsonInput.current?.click()}
              >
                Import JSON
              </Button>
              <input
                ref={jsonInput}
                type="file"
                accept=".json,application/json"
                className="hidden"
                aria-label="Saved map JSON"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  if (file) void importJson(file);
                }}
              />
            </>
          )}
          {importMode === "ai" && !processPage && (
            <>
              <FloorplanImportSetup
                file={file}
                disabled={busy || applying}
                onFile={chooseFile}
                high={highDetail}
                onHigh={setHighDetail}
                regions={useRegions}
                onRegions={setUseRegions}
                wallsOnly={wallsOnly}
                onWallsOnly={setWallsOnly}
                fast={fastMode}
                onFast={setFastMode}
              >
                {isPdf && (
                  <label className="flex items-center gap-2 text-sm">
                    PDF page
                    <input
                      aria-label="PDF page"
                      type="number"
                      min={1}
                      max={pdf?.document.numPages || 1}
                      value={pdfPage}
                      className="w-16 rounded border p-1"
                      disabled={loadingPdf || busy || applying}
                      onChange={(event) => {
                        const page = Number(event.target.value);
                        if (
                          !Number.isInteger(page) ||
                          page < 1 ||
                          page > (pdf?.document.numPages || 1)
                        )
                          return;
                        setPdfPage(page);
                        setComparison(undefined);
                        setResult(undefined);
                        setRegional(undefined);
                        setSourcePng(undefined);
                        setSource(undefined);
                        setError("");
                      }}
                    />
                    {pdf ? `of ${pdf.document.numPages}` : ""}
                    {loadingPdf
                      ? "Reading PDF…"
                      : preparingPdf
                        ? "Preparing page…"
                        : ""}
                  </label>
                )}
              </FloorplanImportSetup>
              <Button
                disabled={
                  !file ||
                  busy ||
                  applying ||
                  (isPdf && (loadingPdf || preparingPdf || !sourcePng))
                }
                className="ml-auto bg-blue-600 text-white hover:bg-blue-700"
                onClick={generate}
              >
                {busy
                  ? "Reconstructing…"
                  : result
                    ? "Try again"
                    : "Generate map"}
              </Button>
              {(comparison || busy) && (
                <Button variant="outline" onClick={() => setProcessPage(true)}>
                  {busy ? "View progress" : "View result"}
                </Button>
              )}
              {busy && <Button onClick={cancelImport}>Cancel</Button>}
            </>
          )}
        </div>
        {error && (
          <p role="alert" className="text-red-600 text-sm">
            {error}
          </p>
        )}
        {!processPage && importMode === "ai" && isPdf && source && (
          <img
            src={source}
            alt="Uploaded floorplan"
            className="h-64 w-full rounded-xl border border-neutral-200 bg-white object-contain"
          />
        )}
        {(processPage || importMode === "json") && (
          <>
            {regional && (
              <ImportRegionProgress
                snapshot={regional}
                selected={selectedRegion}
                onSelect={chooseRegion}
                scope={retryScope}
                onScope={setRetryScope}
                drawing={retryDrawing}
                onDraw={() => {
                  setRetryDrawing(!retryDrawing);
                  setExpanded(false);
                }}
                bounds={retryBounds}
                onRetry={() => retryRegion()}
                onResume={() => retryRegion(true)}
                busy={busy}
                applying={applying}
              />
            )}
            {busy && !comparison && (
              <p role="status" className="text-sm">
                {progress}
              </p>
            )}
            {comparison && <FloorplanImportStatus snapshot={comparison} />}
            {processPage && busy && (
              <Button
                variant="outline"
                className="w-fit"
                onClick={cancelImport}
              >
                Cancel import
              </Button>
            )}
            {(source || result) && (
              <div
                className={`grid gap-4 ${expanded || !source ? "" : "md:grid-cols-2"}`}
              >
                <figure
                  className={
                    (expanded && !!result) || !source ? "hidden" : "min-w-0"
                  }
                >
                  <figcaption className="mb-2 font-medium text-sm">
                    Source image
                  </figcaption>
                  <div className="relative">
                    <img
                      src={source}
                      onLoad={(e) => {
                        setSourceDimensions({
                          width: e.currentTarget.naturalWidth,
                          height: e.currentTarget.naturalHeight
                        });
                      }}
                      alt="Uploaded floorplan"
                      className="h-[360px] w-full rounded border bg-white object-contain"
                    />
                    {comparison?.candidates[0]?.mode === "fast" && (
                      <FastImportSourceOverlay
                        snapshot={comparison}
                        width={sourceDimensions.width}
                        height={sourceDimensions.height}
                      />
                    )}
                    {regional && (
                      <ImportRegionSourceOverlay
                        snapshot={regional}
                        selected={selectedRegion}
                        onSelect={chooseRegion}
                        width={sourceDimensions.width}
                        height={sourceDimensions.height}
                      />
                    )}
                    {regional && (retryDrawing || retryBounds) && (
                      <ImportRetryBox
                        width={sourceDimensions.width}
                        height={sourceDimensions.height}
                        drawing={retryDrawing && !busy}
                        bounds={retryBounds}
                        onCancel={() => {
                          setRetryDrawing(false);
                          setRetryBounds(undefined);
                        }}
                        onChange={(bounds) => {
                          setRetryBounds(bounds);
                          setSelectedRegion("selection");
                          setRetryDrawing(false);
                        }}
                      />
                    )}
                  </div>
                </figure>
                <figure className={!result && expanded ? "hidden" : "min-w-0"}>
                  <figcaption className="mb-2 font-medium text-sm">
                    Generated map
                    {result && (
                      <Button
                        variant="ghost"
                        onClick={() => setReplay((value) => value + 1)}
                      >
                        Replay construction
                      </Button>
                    )}
                    {result && source && (
                      <Button
                        variant="ghost"
                        onClick={() => setExpanded((value) => !value)}
                      >
                        {expanded ? "Side-by-side view" : "Enlarge map"}
                      </Button>
                    )}
                  </figcaption>
                  {result && source && (
                    <label className="mb-2 flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={overlay}
                        onChange={(event) => setOverlay(event.target.checked)}
                      />
                      Show original floorplan underneath
                    </label>
                  )}
                  <div
                    className={`flex ${expanded ? "h-[560px]" : "h-[360px]"} items-center justify-center overflow-hidden rounded border bg-white [&_.map-builder-svg-wrapper]:h-full [&_.map-builder-svg-wrapper]:w-full [&_image]:opacity-40`}
                  >
                    {result && previewMap ? (
                      <MapConstructionPreview
                        replay={replay}
                        arrivals={
                          comparison?.candidates[0]?.mode === "fast"
                            ? arrivals
                            : undefined
                        }
                        key={`${result.map.lastUpdate}-${overlay}-${[...deskIds].join(",")}`}
                        value={previewMap}
                        svgOverlay={
                          <>
                            <ImportRoomHighlightOverlay map={result.map} />
                            {busy &&
                              comparison?.candidates[0]?.mode === "fast" && (
                                <FastImportTiles
                                  snapshot={comparison}
                                  scale={
                                    result.map.editingZoneWidth /
                                    comparison.width
                                  }
                                />
                              )}
                          </>
                        }
                        useSVG
                        hideZoneLegend
                        style={{ width: "100%", height: "100%" }}
                      />
                    ) : (
                      <p className="text-neutral-500 text-sm">
                        Your reconstruction will appear here
                      </p>
                    )}
                  </div>
                </figure>
              </div>
            )}
            {result && (
              <>
                <p className="text-sm">
                  {
                    result.map.shapes.filter((s) => s.type === "wall-path")
                      .length
                  }{" "}
                  wall paths ·{" "}
                  {result.map.shapes.filter((s) => s.type === "spot").length}{" "}
                  bookable spots ·{" "}
                  {
                    result.map.shapes.filter(
                      (s) => s.type === "spot" && s.spotType === "desk"
                    ).length
                  }{" "}
                  desks ·{" "}
                  {result.map.shapes.reduce(
                    (count, shape) =>
                      count +
                      (shape.type === "wall-path"
                        ? shape.edges.reduce(
                            (n, edge) => n + (edge.doors?.length || 0),
                            0
                          )
                        : 0),
                    0
                  )}{" "}
                  doors ·{" "}
                  {result.map.shapes.reduce(
                    (count, shape) =>
                      count +
                      (shape.type === "wall-path"
                        ? shape.edges.reduce(
                            (n, edge) => n + (edge.windows?.length || 0),
                            0
                          )
                        : 0),
                    0
                  )}{" "}
                  windows · {result.map.shapes.length} total objects ·{" "}
                  {result.partial
                    ? "Partial preview"
                    : importedJson
                      ? "Loaded from JSON · no AI requests"
                      : `Completed in ${Math.floor(durationSeconds / 60)}m ${durationSeconds % 60}s`}
                </p>
                {!result.partial &&
                  result.map.shapes.some(
                    (s) => s.type === "spot" && s.spotType === "meeting_room"
                  ) && (
                    <fieldset className="rounded-xl border border-neutral-200 bg-neutral-50/60 p-5">
                      <legend className="px-2 font-medium text-neutral-900 text-sm">
                        Meeting rooms
                      </legend>
                      <div className="mb-4 flex items-center gap-3">
                        <div className="rounded-lg bg-blue-50 p-2 text-blue-600">
                          <DoorOpen size={20} />
                        </div>
                        <p className="text-sm text-neutral-600">
                          Choose how rooms appear on your map.
                        </p>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <label
                          className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${markerOnTable ? "border-blue-300 bg-blue-50/50" : "border-neutral-200 bg-white"}`}
                        >
                          <MapPin
                            size={20}
                            className="mt-0.5 shrink-0 text-blue-600"
                          />
                          <span className="flex-1">
                            <span className="block font-medium text-sm">
                              Simple room marker
                            </span>
                            <span className="mt-1 block text-xs leading-relaxed text-neutral-500">
                              Show just the availability spot over an existing
                              table. Keep the room symbol when there’s no table.
                            </span>
                          </span>
                          <input
                            type="checkbox"
                            aria-label="Marker only when a table is present"
                            className="mt-1 accent-blue-600"
                            checked={markerOnTable}
                            disabled={applying || busy || !!result.partial}
                            onChange={(e) => setMarkerOnTable(e.target.checked)}
                          />
                        </label>
                        <label
                          className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${highlightRoom ? "border-blue-300 bg-blue-50/50" : "border-neutral-200 bg-white"}`}
                        >
                          <ScanLine
                            size={20}
                            className="mt-0.5 shrink-0 text-blue-600"
                          />
                          <span className="flex-1">
                            <span className="block font-medium text-sm">
                              Highlight the whole room
                            </span>
                            <span className="mt-1 block text-xs leading-relaxed text-neutral-500">
                              Use the room’s footprint to show availability.
                              Works together with the room marker.
                            </span>
                          </span>
                          <input
                            type="checkbox"
                            aria-label="Highlight the whole room"
                            className="mt-1 accent-blue-600"
                            checked={highlightRoom}
                            disabled={applying || busy || !!result.partial}
                            onChange={(e) => setHighlightRoom(e.target.checked)}
                          />
                        </label>
                      </div>
                      <p className="mt-3 text-xs text-neutral-500">
                        Markers are centered on the table, or in the room when
                        no table is detected.
                      </p>
                    </fieldset>
                  )}
                {result.warnings.length > 0 && (
                  <ImportNotes
                    items={result.warnings}
                    title={`${new Set(result.warnings).size} note${new Set(result.warnings).size === 1 ? "" : "s"} from the reconstruction`}
                  />
                )}
                {!result.partial && (
                  <>
                    <p className="text-neutral-600 text-sm">
                      Generated spots are unlinked. Saving later creates new
                      resources; activating can remove existing resources absent
                      from this map. Applying here only changes the editor.
                    </p>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={download}
                        disabled={busy || !!result.partial}
                      >
                        Download JSON
                      </Button>
                      <Button
                        disabled={
                          applying ||
                          busy ||
                          !!result.partial ||
                          (!sourcePng && !importedJson)
                        }
                        className="bg-blue-600 text-white hover:bg-blue-700"
                        onClick={apply}
                      >
                        {applying
                          ? `Saving assets ${uploadProgress.completed}/${uploadProgress.total}…`
                          : shapesRef.current.length > 0
                            ? "Apply and replace"
                            : "Apply to editor"}
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
